import React, {
  Fragment,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useForm } from "react-hook-form";
import "react-toastify/dist/ReactToastify.css";
import { Link, useLocation, useNavigate } from "react-router-dom";

import Spinner from "../FieldComponets/Spinner";
import { ErrorMessage } from "@hookform/error-message";
import { useDispatch, useSelector } from "react-redux";
import ToastDefault from "../FieldComponets/Toast";
import PageLayout from "../Layouts/PageLayout";
import secureLocalStorage from "react-secure-storage";
import {
  getDate,
  getItemFromStorage,
  setItemToStorage,
  WeekDayFormat,
  TimeDisplay,
} from "../../utils/Helper";
import {
  DateFormat,
  getAdminTimezone,
  getTimeZoneFromStorage,
} from "../../utils/DateTimeHelper";
import momentTimezone from "moment-timezone";
import { SalonApprovedStatusAction } from "../../Redux/Action/SalonAction";
import Swal from "sweetalert2";
import { StorageConstant, VALIDATION_MESSAGE } from "../../Constants/Constants";
import { BASE_URL, url } from "../../Config/Config";
import {
  ImageUrl,
  decodeID,
  getLastURL,
  replaceToString,
} from "../../utils/Helper";
import { paginationQuery } from "../../utils/GlobalHelper";
import {
  SalonDetailsAction,
  SalonSubscriptionManageAction,
} from "../../Redux/Action/SalonAction";
import { SubscriptionPlanListAction } from "../../Redux/Action/SubscriptionPlanAction";
import { Modal } from "react-bootstrap";
import ImageViewer from "react-simple-image-viewer";
import { toast } from "react-toastify";
import { DashboardRedirectionAction } from "../../Redux/Action/CommonAction";

/** `getAdminTimezone()` may return a string or a parsed object depending on storage. */
function resolveAdminIanaTimezone(): string {
  let raw: unknown;
  try {
    raw = getAdminTimezone() as unknown;
  } catch {
    raw = null;
  }
  let s = "";
  if (typeof raw === "string") {
    s = raw.trim();
  } else if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const v =
      o.value ?? o.timezone ?? o.timeZone ?? o.tz ?? o.name ?? o.label;
    if (v != null) s = String(v).trim();
  } else if (raw != null && raw !== "") {
    s = String(raw).trim();
  }
  if (s && momentTimezone.tz.zone(s)) return s;
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** Calendar date as YYYY-MM-DD (UTC-safe for DATE / ISO strings from API). */
function blockDateToYmd(d: any): string | null {
  if (d == null || d === "") return null;
  if (typeof d === "string") {
    const m = d.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  const dt = momentTimezone.utc(d);
  if (!dt.isValid()) return null;
  return dt.format("YYYY-MM-DD");
}

/** TIME / string → HH:mm:ss for combining with date in UTC (same idea as `setDateTime` in DateTimeHelper). */
function blockTimeToHms(t: any): string {
  if (t == null || t === "") return "00:00:00";
  if (typeof t === "string") {
    const s = t.trim();
    const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (m) {
      const hh = String(m[1]).padStart(2, "0");
      const mm = String(m[2]).padStart(2, "0");
      const ss = String(m[3] || "00").padStart(2, "0");
      return `${hh}:${mm}:${ss}`;
    }
  }
  const dt = momentTimezone.utc(t);
  if (dt.isValid()) return dt.format("HH:mm:ss");
  return "00:00:00";
}

function formatBlockTimeStrPlain(t: any): string {
  if (t == null || t === "") return "—";
  const s = String(t).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  return "—";
}

/** Show block start/end in admin-selected timezone (same stack as subscription/event helpers). */
function formatBlockRangeInAdminTz(block: any): {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
} {
  if (!block) {
    return { startDate: "—", endDate: "—", startTime: "—", endTime: "—" };
  }
  const ymdS = blockDateToYmd(block.start_date);
  const ymdE = blockDateToYmd(block.end_date);
  if (!ymdS || !ymdE) {
    return {
      startDate: block.start_date ? DateFormat(block.start_date) : "—",
      endDate: block.end_date ? DateFormat(block.end_date) : "—",
      startTime: formatBlockTimeStrPlain(block.start_time),
      endTime: formatBlockTimeStrPlain(block.end_time),
    };
  }
  const tz = resolveAdminIanaTimezone();
  const hmsS = blockTimeToHms(block.start_time);
  const hmsE = blockTimeToHms(block.end_time);
  try {
    const startUtc = momentTimezone.tz(
      `${ymdS} ${hmsS}`,
      "YYYY-MM-DD HH:mm:ss",
      true,
      "UTC",
    );
    const endUtc = momentTimezone.tz(
      `${ymdE} ${hmsE}`,
      "YYYY-MM-DD HH:mm:ss",
      true,
      "UTC",
    );
    if (!startUtc.isValid() || !endUtc.isValid()) {
      throw new Error("invalid block datetime");
    }
    const startTz = startUtc.clone().tz(tz);
    const endTz = endUtc.clone().tz(tz);
    if (
      !startTz ||
      typeof startTz.format !== "function" ||
      !endTz ||
      typeof endTz.format !== "function" ||
      !startTz.isValid() ||
      !endTz.isValid()
    ) {
      throw new Error("invalid tz conversion");
    }
    return {
      startDate: startTz.format("MM-DD-YYYY"),
      endDate: endTz.format("MM-DD-YYYY"),
      startTime: startTz.format("HH:mm"),
      endTime: endTz.format("HH:mm"),
    };
  } catch {
    return {
      startDate: block.start_date ? DateFormat(block.start_date) : "—",
      endDate: block.end_date ? DateFormat(block.end_date) : "—",
      startTime: formatBlockTimeStrPlain(block.start_time),
      endTime: formatBlockTimeStrPlain(block.end_time),
    };
  }
}

const isPastDate = (v: any): boolean => {
  if (!v) return false;
  const t = new Date(v).getTime();
  return Number.isFinite(t) && t < Date.now();
};

function SalonDetails() {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const editId = getLastURL(location);
  const log_in = useSelector((state: any) => state.SalonDetailGetState);
  const updateStatus = useSelector((state: any) => state.SalonStatusGetState);
  const salonSubManage = useSelector(
    (state: any) => state.SalonSubscriptionManageState,
  );
  const salondata = useSelector(
    (state: any) => state.SalonDetailGetState?.data?.data?.detail,
  );
  const [weekday, setWeekday] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentbuisnessImage, setCurrentbuisnessImage] = useState(0);
  const [isViewerbuisnessOpen, setIsViewerbuisnessOpen] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [buisnessimages, setBuisnessImages] = useState<string[]>([]);
  const [ownerImages, setOwnerImages] = useState<any>([]);
const [currentOwnerImage, setCurrentOwnerImage] = useState(0);
const [isOwnerViewerOpen, setIsOwnerViewerOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const planListState = useSelector(
    (state: any) => state.SubscriptionPlanListState,
  );
  const offlinePlans: any[] = planListState?.data?.data?.rows ?? [];

  const handleStatusChnage = async (e: any) => {
    if (e == 2) {
      setOpen(true);
    } else {
      Swal.fire({
        title: VALIDATION_MESSAGE?.ARE_YOU_SURE,
        text:
          VALIDATION_MESSAGE?.ARE_YOU_LOGOUT_ANY + " Change Approved Status ?",
        icon: "warning",
        // showCancelButton: true,
        confirmButtonColor: "#31CE36",
        cancelButtonColor: "#d33",
        showDenyButton: true,
        denyButtonText: "No",
        confirmButtonText: "Yes",
      }).then(async (result) => {
        if (result.isConfirmed) {
          const data = {
            status: e,
            status_id: salondata.id as any, // Assuming id is of type any
          };
          dispatch(SalonApprovedStatusAction(data));
        } else {
          $(".profile-approve-dropdown").val(
            salondata?.is_approved == 3 ? 0 : salondata?.is_approved,
          );
        }
      });
    }
  };

  useEffect(() => {
    if (salondata?.opt_days) {
      setTimeout(() => {
        const jsondata = salondata?.opt_days?.map((res: any) => ({
          day: res.day,
          start_time: res.start_time,
          end_time: res.end_time,
        }));
        const data: any = JSON.stringify(jsondata);
        setWeekday(WeekDayFormat(data));
      }, 2000);
    }

    if (
      salondata?.categories.length > 0 &&
      salondata?.categories != undefined
    ) {
      salondata?.categories.forEach((res: any) => {
        if (res?.category_detail != undefined) {
          const filePath = res?.category_detail?.image_details?.file_path;
          setImages((images) => [...images, filePath]);
        }
      });
    }

    if (salondata?.buisness_images.length > 0) {
      salondata?.buisness_images.forEach((res: any) => {
        const filePath = res?.file_path;
        setBuisnessImages((buisnessimages) => [...buisnessimages, filePath]);
      });
    }

    if (salondata?.owner_image?.file_path) {
      setOwnerImages([salondata.owner_image.file_path]);
    }
  }, [salondata]);
  useEffect(() => {
    setLoading(true);
    var params = {
      detail_id: decodeID(editId),
    };
    if (editId) {
      dispatch(SalonDetailsAction(params));
      setTimeout(() => {
        setLoading(false);
      }, 400);
    }
  }, [dispatch, editId]);

  useEffect(() => {
    const query: any = paginationQuery(0, 500, [{ id: "id", desc: false }], "", []);
    dispatch(SubscriptionPlanListAction(query));
  }, [dispatch]);

  useEffect(() => {
    if (salondata?.offline_subscription_plan_id != null) {
      setSelectedPlanId(String(salondata.offline_subscription_plan_id));
    } else {
      setSelectedPlanId("");
    }
  }, [salondata?.id, salondata?.offline_subscription_plan_id]);

  const handelReason = (e: any) => {
    const data = {
      status: e,
      status_id: salondata.id as any, // Assuming id is of type any
      reject_reason: reason,
    };
    if (
      data.reject_reason == "" ||
      data.reject_reason == undefined ||
      data.reject_reason.trim() === ""
    ) {
      setError("Please Enter Reject Reason");
    } else {
      dispatch(SalonApprovedStatusAction(data));
    }
  };
  useEffect(() => {
    if (updateStatus?.data?.status) {
      setOpen(false);
      setError("");
      var params = {
        detail_id: decodeID(editId),
      };
      if (editId) {
        dispatch(SalonDetailsAction(params));
      }
    }
  }, [updateStatus?.data]);

  useEffect(() => {
    if (!salonSubManage?.status || !editId) return;
    dispatch(
      SalonDetailsAction({
        detail_id: decodeID(editId),
      }),
    );
  }, [salonSubManage?.status, editId, dispatch]);

  const resolvePlanIdForAction = (): number | null => {
    if (selectedPlanId) {
      const n = parseInt(selectedPlanId, 10);
      return Number.isFinite(n) ? n : null;
    }
    const fromSalon = salondata?.offline_subscription_plan_id;
    if (fromSalon != null) {
      const n = parseInt(String(fromSalon), 10);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };

  const handleAssignPlanOnly = () => {
    if (!salondata?.id) return;
    const planId = resolvePlanIdForAction();
    if (!planId) {
      toast.error("Select a subscription plan first.");
      return;
    }
    Swal.fire({
      title: "Save plan on this salon?",
      text: "Updates the assigned plan only. Use Activate subscription when you want access to start from now.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Save plan",
    }).then((r) => {
      if (r.isConfirmed) {
        dispatch(
          SalonSubscriptionManageAction({
            salon_id: salondata.id,
            plan_id: planId,
            _action: "assign-plan",
          }),
        );
      }
    });
  };

  const handleSubActivate = () => {
    if (!salondata?.id) return;
    const planId = resolvePlanIdForAction();
    if (!planId) {
      toast.error("Select a plan or save an assigned plan before activating.");
      return;
    }
    Swal.fire({
      title: "Activate subscription?",
      text: "Starts validity from now using the selected plan (and saves it on the salon if needed).",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Activate",
    }).then((r) => {
      if (r.isConfirmed) {
        dispatch(
          SalonSubscriptionManageAction({
            salon_id: salondata.id,
            plan_id: planId,
          }),
        );
      }
    });
  };

  const handleSubSuspend = () => {
    if (!salondata?.id) return;
    Swal.fire({
      title: "Suspend subscription?",
      text: "The salon will lose access until you activate again.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Suspend",
    }).then((r) => {
      if (r.isConfirmed) {
        dispatch(
          SalonSubscriptionManageAction({
            salon_id: salondata.id,
            _action: "suspend",
          }),
        );
      }
    });
  };

  const openImageViewer = useCallback((index: any) => {
    console.log(index);

    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  const handleClose = () => {
    $(".profile-approve-dropdown").val(
      salondata?.is_approved == 3 ? 0 : salondata?.is_approved,
    );
    setOpen(false);
    setError("");
  };
  const openbuisnessImageViewer = useCallback((index: any) => {
    setCurrentbuisnessImage(index);
    setIsViewerbuisnessOpen(true);
  }, []);

  const closebuisnessImageViewer = () => {
    setCurrentbuisnessImage(0);
    setIsViewerbuisnessOpen(false);
  };

  const openOwnerImageViewer = useCallback((index: any) => {
    setCurrentOwnerImage(index);
    setIsOwnerViewerOpen(true);
  }, []);

  const closeOwnerImageViewer = () => {
    setCurrentOwnerImage(0);
    setIsOwnerViewerOpen(false);
  };

  

  useEffect(() => {
    return () => {
      dispatch(SalonDetailsAction("RESET"));
    };
  }, []);

  const blockServiceData = salondata?.block_Service_data;
  const isFullSalonBlock = Number(salondata?.is_block_service) === 1;
  const isFullyOccupiedUnavailability =
    blockServiceData &&
    String(blockServiceData.unavailability) === "1";
  const isNotAvailableUnavailability =
    blockServiceData &&
    String(blockServiceData.unavailability) === "2";
  const showSalonFullBlockSection =
    isFullSalonBlock ||
    isFullyOccupiedUnavailability ||
    isNotAvailableUnavailability;

  const blockTzDisplay = useMemo(
    () => formatBlockRangeInAdminTz(blockServiceData),
    [blockServiceData],
  );

  const unavailabilityLabel = (u: any) => {
    const v = String(u ?? "");
    if (v === "1") return "Fully occupied";
    if (v === "2") return "Not available";
    return "—";
  };

  const subscriptionExpiredByDate = isPastDate(salondata?.subscription_expires_at);
  const displayPlanStatus = subscriptionExpiredByDate
    ? "Expired"
    : salondata?.subscription_plan_status || "—";
  const displayPlanName = subscriptionExpiredByDate
    ? salondata?.subscription_plan_name ||
      salondata?.offline_subscription_plan?.name ||
      "—"
    : salondata?.offline_subscription_plan?.name ||
      salondata?.subscription_plan_name ||
      "—";

  return (
    <Fragment>
      {loading || log_in?.loading || salonSubManage?.loading ? (
        <Spinner />
      ) : (
        ""
      )}
      <PageLayout
        title="Salon Management"
        breadcrumbs={{
          secLast: { link: "/salon-list", name: "Salon Management" },
        }}
        buttons={[
          {
            url: "/class-add",
            className: "btn btn-primary",
            text: "+ Add Sponsors",
            show: false,
          }, // This button will be shown
        ]}
      >
        <div className="card">
          <div className="card-body">
            <div className="row gy-4">
              <div className="col-12">
                <h5>Registration Details</h5>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Salon Owner ID :
                    </label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>{salondata?.u_id ? salondata?.u_id : "--"}</span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Salon Name :
                    </label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.salon_name ? salondata?.salon_name : "--"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Email Address :
                    </label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>{salondata?.email ? salondata?.email : "--"} </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Phone Number :
                    </label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata
                        ? `${salondata?.phone_country_code || "--"} ${salondata.phone_number || "--"}`
                        : "--"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="card mt-3">
          <div className="card-body">
            <div className="row gy-4">
              <div className="col-12 d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h5 className="mb-0">Subscription details</h5>
                <div className="d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={handleSubActivate}
                  >
                    Activate subscription
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-warning"
                    onClick={handleSubSuspend}
                  >
                    Suspend subscription
                  </button>
                </div>
              </div>
              <div className="col-12">
                <div className="p-3 bg-light rounded border">
                  <label className="form-label fw-semibold mb-2 d-block">
                    Assign or change subscription plan
                  </label>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <select
                      className="form-select shadow-none"
                      style={{ maxWidth: "22rem" }}
                      value={selectedPlanId}
                      onChange={(e) => setSelectedPlanId(e.target.value)}
                      disabled={planListState?.loading}
                    >
                      <option value="">Select plan…</option>
                      {offlinePlans.map((p: any) => (
                        <option key={p.id} value={String(p.id)}>
                          {p.name}
                          {p.price != null
                            ? ` (€${Number(p.price).toFixed(2)})`
                            : ""}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={!selectedPlanId}
                      onClick={handleAssignPlanOnly}
                    >
                      Save plan on salon
                    </button>
                  </div>
                  <p className="small text-muted mb-0 mt-2">
                    Saves the plan on the salon record without changing dates or
                    status. Choose a plan, save if needed, then use{" "}
                    <strong>Activate subscription</strong> to grant access from
                    now (or change plan and activate again to renew with a
                    different plan).
                  </p>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label className="form-label">Subscription name :</label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>{displayPlanName}</span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label className="form-label">Purchase date :</label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.subscription_purchase_date
                        ? getDate(salondata.subscription_purchase_date)
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label className="form-label">End date :</label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.subscription_expires_at
                        ? getDate(salondata.subscription_expires_at)
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label className="form-label">Subscription type :</label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.subscription_plan_type
                        ? salondata.subscription_plan_type
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label className="form-label">Plan status :</label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {displayPlanStatus}
                      {salondata?.subscription_status_label &&
                      salondata?.subscription_status_label !==
                        salondata?.subscription_plan_status
                        ? ` (${salondata.subscription_status_label})`
                        : ""}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label className="form-label">Active from :</label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.subscription_active_date
                        ? getDate(salondata.subscription_active_date)
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label className="form-label">Suspended at :</label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.subscription_suspended_at
                        ? getDate(salondata.subscription_suspended_at)
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label className="form-label">Plan price :</label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.offline_subscription_plan?.price != null
                        ? String(salondata.offline_subscription_plan.price)
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label className="form-label">Validity (days) :</label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.offline_subscription_plan?.validity_days !=
                      null
                        ? String(
                            salondata.offline_subscription_plan.validity_days,
                          )
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-12">
                <div className="row">
                  <div className="col-md-2 col-lg-2 col-xl-2">
                    <label className="form-label">Overview :</label>
                  </div>
                  <div className="col-md-10 col-lg-10 col-xl-10">
                    <span className="d-block text-break">
                      {salondata?.offline_subscription_plan?.overview
                        ? salondata.offline_subscription_plan.overview
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="row gy-4">
              <div className="col-12">
                <h5>Salon Details</h5>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Location :
                    </label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.location ? salondata?.location : "--"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Salon Description :
                    </label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.description ? salondata?.description : "--"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Services{" "}
                    </label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    {/* <span>{salondata?.services ? JSON.parse(salondata?.services).join() : '--'}</span> */}
                    {/* <span>{categoriesnames.length > 0 ? categoriesnames.join() : '--'}</span> */}
                    {salondata?.services
                      ? JSON.parse(salondata?.services).map(
                          (res: any, i: any) => (
                            <div className="service-tag" key={i}>
                              {res}
                            </div>
                          ),
                        )
                      : "----"}
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Salon Owner Name :
                    </label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.owner_name ? salondata?.owner_name : "--"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Salon Owner Experience :
                    </label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.owner_experience
                        ? salondata?.owner_experience
                        : "--"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Registration Date :
                    </label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.created_at
                        ? getDate(salondata?.created_at)
                        : "--"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Email Verify Date :
                    </label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.email_verified_at
                        ? getDate(salondata?.email_verified_at)
                        : "--"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-xl-6">
                <div className="row">
                  <div className="col-md-4 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Proof Of Ownership
                    </label>
                  </div>
                  <div className="col-md-8 col-xl-9">
                    <div className="cat-img-view-row">
                    {ownerImages.length > 0 ? (
                      ownerImages.map((img: any, index: any) => (
                        <div className="cat-img-view" key={index}>
                          <img
                            src={img}
                            onClick={() => openOwnerImageViewer(index)}
                            className="rounded-circle"
                            alt=""
                          />
                        </div>
                      ))
                    ) : (
                      "No Proof Of Ownership"
                    )}
                  </div>
                    {isOwnerViewerOpen && (
                        <ImageViewer
                          src={ownerImages}
                          currentIndex={currentOwnerImage}
                          onClose={closeOwnerImageViewer}
                          disableScroll={false}
                          backgroundStyle={{
                            backgroundColor: "rgba(0,0,0,0.9)",
                          }}
                          closeOnClickOutside={true}
                        />
                      )}
                  </div>
                </div>
              </div>
              <div className="col-xl-6">
                <div className="row">
                  <div className="col-md-4 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Categories
                    </label>
                  </div>
                  <div className="col-md-8 col-xl-9">
                    <div className="cat-img-view-row">
                      {salondata?.categories.length > 0
                        ? salondata?.categories.map((res: any, index: any) => (
                            // console.log(res)
                            <div className="cat-img-view" key={index}>
                              <img
                                src={
                                  res?.category_detail?.image_details?.file_path
                                }
                                onClick={() => openImageViewer(index)}
                                className="rounded-circle"
                                alt=""
                              />
                              <div className="mt-2">
                                {res?.category_detail?.name}
                              </div>
                            </div>
                          ))
                        : "No categories"}
                    </div>
                    {isViewerOpen && (
                      <ImageViewer
                        src={images}
                        currentIndex={currentImage}
                        onClose={closeImageViewer}
                        disableScroll={true}
                        backgroundStyle={{
                          backgroundColor: "rgba(0,0,0,0.9)",
                        }}
                        closeOnClickOutside={true}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="col-xl-6">
                <div className="row">
                  <div className="col-md-4 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Business Images
                    </label>
                  </div>
                  <div className="col-md-8 col-xl-9">
                    <div className="cat-img-view-row">
                      {salondata?.buisness_images.length > 0
                        ? salondata?.buisness_images.map(
                            (res: any, index: any) => (
                              // console.log(res)
                              <div className="cat-img-view" key={index}>
                                <img
                                  src={res?.file_path}
                                  onClick={() => openbuisnessImageViewer(index)}
                                  className="rounded-circle"
                                  alt=""
                                />
                              </div>
                            ),
                          )
                        : "No Business Images"}
                    </div>
                    {isViewerbuisnessOpen && (
                      <ImageViewer
                        src={buisnessimages}
                        currentIndex={currentbuisnessImage}
                        onClose={closebuisnessImageViewer}
                        disableScroll={false}
                        backgroundStyle={{
                          backgroundColor: "rgba(0,0,0,0.9)",
                        }}
                        closeOnClickOutside={true}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="row gy-4">
              <div className="col-12">
                <h5>Salon Status Details</h5>
              </div>

              <div className="col-md-6">
                <div className="row">
                  <div className="col-md-3">
                    <label htmlFor="username" className="form-label">
                      Status :
                    </label>
                  </div>
                  <div className="col-md-9">
                    <span>
                      {salondata?.status == "1" ? "Active" : "InActive"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="row">
                  <div className="col-md-3">
                    <label htmlFor="username" className="form-label">
                      Min Price :
                    </label>
                  </div>
                  <div className="col-md-9">
                    <span>
                      {salondata?.min_price ? salondata?.min_price : 0}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="row">
                  <div className="col-md-3">
                    <label htmlFor="username" className="form-label">
                      Max Price :
                    </label>
                  </div>
                  <div className="col-md-9">
                    <span>
                      {salondata?.max_price ? salondata?.max_price : 0}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6" style={{ cursor: "cell" }}>
                <Link
                  to={"/service"}
                  onClick={() =>
                    dispatch(
                      DashboardRedirectionAction({ salon_id: salondata?.id }),
                    )
                  }
                >
                  <div className="row">
                    <div className="col-md-4 col-lg-5 col-xl-3">
                      <label htmlFor="username" className="form-label">
                        Salon Total Services :
                      </label>
                    </div>
                    <div className="col-md-8 col-lg-7 col-xl-9">
                      <span>
                        {salondata?.totalservices
                          ? salondata?.totalservices
                          : 0}
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
              <div className="col-lg-6" style={{ cursor: "cell" }}>
                <Link
                  to={"/booking-list"}
                  onClick={() =>
                    dispatch(
                      DashboardRedirectionAction({ salon_id: salondata?.id }),
                    )
                  }
                  //   to={`/booking-list?salon_id=${salondata?.id}`}
                >
                  <div className="row">
                    <div className="col-md-4 col-lg-5 col-xl-3">
                      <label htmlFor="username" className="form-label">
                        Salon Total Booking :
                      </label>
                    </div>
                    <div className="col-md-8 col-lg-7 col-xl-9">
                      <span>
                        {salondata?.totalbooking ? salondata?.totalbooking : 0}
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Salon Staff Member :
                    </label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.totalStaffmember
                        ? salondata?.totalStaffmember
                        : 0}
                    </span>
                  </div>
                </div>
              </div>

              <div className="col-lg-6" style={{ cursor: "cell" }}>
                <Link
                  to={"/review-rating-list"}
                  onClick={() =>
                    dispatch(
                      DashboardRedirectionAction({ salon_id: salondata?.id }),
                    )
                  }
                  //   to={`/booking-list?salon_id=${salondata?.id}`}
                >
                  <div className="row">
                    <div className="col-md-4 col-lg-5 col-xl-3">
                      <label htmlFor="username" className="form-label">
                        Total Ratings & Reviews :
                      </label>
                    </div>
                    <div className="col-md-8 col-lg-7 col-xl-9">
                      <span>
                        {salondata?.avg_rating 
                          ? Number(salondata.avg_rating).toFixed(2) 
                          : "0.00"}{" "}
                        ({salondata?.totalReviewRating || 0})
                      </span>
                    </div>
                  </div>
                </Link>
              </div>

              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4 col-lg-5 col-xl-3">
                    <label htmlFor="username" className="form-label">
                      Salon Total Favorites :
                    </label>
                  </div>
                  <div className="col-md-8 col-lg-7 col-xl-9">
                    <span>
                      {salondata?.totalFavourites
                        ? salondata?.totalFavourites
                        : 0}
                    </span>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <label htmlFor="status" className="form-label">
                  Approved Status
                </label>
                <select
                  name="users"
                  id="approved"
                  disabled={salondata?.owner_name != null ? false : true}
                  className="form-control profile-approve-dropdown"
                  onChange={(e: any) => handleStatusChnage(e.target.value)}
                >
                  <option
                    value="0"
                    selected={
                      salondata?.is_approved === 0 ||
                      salondata?.is_approved === 3
                        ? true
                        : false
                    }
                  >
                    Pending
                  </option>
                  <option
                    value="1"
                    selected={salondata?.is_approved == 1 ? true : false}
                  >
                    Approved
                  </option>
                  <option
                    value="2"
                    selected={salondata?.is_approved == 2 ? true : false}
                  >
                    Rejected
                  </option>
                  {/* <option value="3" selected={salondata?.is_approved == 3 ? true : false}  >Resubmit</option> */}
                </select>
                <p style={{ color: "red" }}>
                  {salondata?.owner_name == null
                    ? "Note: This salon user has not completed their profile. You cannot change this status."
                    : null}
                </p>
              </div>

              <div className="col-md-6"></div>
              {showSalonFullBlockSection ? (
                <div className="col-md-6">
                  <div className="card border-warning">
                    <div className="card-header bg-warning bg-opacity-10">
                      <strong>Salon full block details</strong>
                      <span
                        className={`badge ms-2 ${
                          isFullSalonBlock
                            ? "bg-warning text-dark"
                            : "bg-secondary"
                        }`}
                      >
                        {isFullSalonBlock
                          ? "Full salon block enabled"
                          : "Full salon block disabled"}
                      </span>
                    </div>
                    <div className="card-body py-3">
                      {blockServiceData ? (
                        <div className="table-responsive">
                          <table className="table table-sm table-bordered mb-0">
                            <tbody>
                              <tr>
                                <th
                                  scope="row"
                                  className="bg-light"
                                  style={{ width: "220px" }}
                                >
                                  Unavailability type
                                </th>
                                <td>
                                  {unavailabilityLabel(
                                    blockServiceData.unavailability,
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <th scope="row" className="bg-light">
                                  Start date
                                </th>
                                <td>{blockTzDisplay.startDate}</td>
                              </tr>
                              <tr>
                                <th scope="row" className="bg-light">
                                  End date
                                </th>
                                <td>{blockTzDisplay.endDate}</td>
                              </tr>
                              <tr>
                                <th scope="row" className="bg-light">
                                  Start time
                                </th>
                                <td>{blockTzDisplay.startTime}</td>
                              </tr>
                              <tr>
                                <th scope="row" className="bg-light">
                                  End time
                                </th>
                                <td>{blockTzDisplay.endTime}</td>
                              </tr>
                              <tr>
                                <th scope="row" className="bg-light">
                                  Reason
                                </th>
                                <td>
                                  {blockServiceData.reason
                                    ? String(blockServiceData.reason)
                                    : "—"}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="mb-0 text-muted">
                          Full salon block is on. No dated block record is
                          stored for this salon.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="col-xl-6">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Operational Days of Salon</th>
                      <th scope="col">
                        Operational Hours of Salon(Start Time)
                      </th>
                      <th scope="col">Operational Hours of Salon(End Time)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weekday.length > 0 ? (
                      weekday.map((res: any, i: any) => (
                        <tr key={i}>
                          <td>{res.day}</td>
                          <td>{TimeDisplay(res.start_time)}</td>
                          <td>{TimeDisplay(res.end_time)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        {" "}
                        <td>DATA NOT FOUND</td> <td>DATA NOT FOUND</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-3 ">
          <Link
            to={"/salon-list"}
            type="submit"
            className="btn btn-primary waves-effect waves-light"
          >
            BACK
          </Link>
        </div>

        <Modal show={open}>
          <Modal.Header closeButton onClick={() => handleClose()}>
            <Modal.Title>Rejected Reason</Modal.Title>
          </Modal.Header>
          <div className="modal-body">
            <div className="row mb-3">
              <div className="col-12">
                <label className="form-label mb-0">Rejected Reason</label>
                <input
                  type="text"
                  maxLength={100}
                  className="form-control"
                  required
                  onChange={(e) => setReason(e.target.value)}
                />
                <span style={{ color: "red" }}>{error}</span>
              </div>
              <div className="col-12 mt-2">
                <a
                  className="btn theme-bg float-start"
                  style={{ color: "white" }}
                  onClick={() => handelReason("2")}
                >
                  SEND
                </a>
              </div>
            </div>
          </div>
        </Modal>
        <ToastDefault />
      </PageLayout>
    </Fragment>
  );
}

export default SalonDetails;
